import { useState } from 'react';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import type { RBACGroup } from '../../../types';
import GroupDisplayItem from '../../users/components/GroupDisplayItem';
import {
  selectACLPendingAdds,
  selectACLPendingDeletes,
  selectSelectedACLRoleGroupId,
  selectACLRole,
  addRoleToACLPendingDeletes,
} from '../services/aclMembershipSlice';

type Props = {
  groups?: RBACGroup[];
  // On-prem-mastered ACLs are managed in AD — show the nested roles but offer no delete
  // control (the membership write would fail). The role-add picker and Submit are gated
  // separately on the ACL page. See isOnPremGroup.
  readOnly?: boolean;
};

export default function ACLRoleList({ groups, readOnly = false }: Props) {
  const pendingAdds = useAppSelector(selectACLPendingAdds);
  const pendingDeletes = useAppSelector(selectACLPendingDeletes);
  const selectedGroupId = useAppSelector(selectSelectedACLRoleGroupId);
  const dispatch = useAppDispatch();
  const [query, setQuery] = useState('');

  if (!groups) {
    return <div></div>;
  }

  // Pending adds (green) first, then the assigned members alphabetically.
  const allGroups = [
    ...pendingAdds,
    ...[...groups].sort((a, b) => a.displayName.localeCompare(b.displayName)),
  ];
  const q = query.trim().toLowerCase();
  const visible = q
    ? allGroups.filter((group) => group.displayName.toLowerCase().includes(q))
    : allGroups;

  return (
    <div className="bg-slate-50 dark:bg-gray-800 rounded-lg p-4 flex flex-col max-h-[calc(100vh-16rem)]">
      <div className="flex flex-row flex-wrap items-center gap-2 pb-2">
        <span className="font-bold">Role group members:</span>
        {allGroups.length > 0 && (
          <input
            type="text"
            className="input input-bordered input-sm w-56 ml-auto focus:outline-none"
            placeholder="Search role groups"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        )}
      </div>
      {allGroups.length === 0 ? (
        <div className="text-sm opacity-60">No Role groups assigned to this ACL.</div>
      ) : visible.length === 0 ? (
        <div className="text-sm opacity-60">No matching role groups.</div>
      ) : (
        <div className="border border-base-200 rounded-lg overflow-hidden flex-1 min-h-0">
          <ul className="divide-y divide-base-200 w-full h-full overflow-y-auto">
            {visible.map((group) => {
              const isPendingAdd = pendingAdds.some((g) => g.id === group.id);
              const isPendingDelete = pendingDeletes.some((g) => g.id === group.id);
              const key = isPendingAdd ? group.id + '_add' : group.id;
              return (
                <GroupDisplayItem
                  key={key}
                  displayName={group.displayName}
                  roleId={group.id}
                  selected={selectedGroupId === group.id}
                  added={isPendingAdd}
                  deleted={isPendingDelete}
                  onClick={() => dispatch(selectACLRole(group))}
                  onDelete={
                    readOnly ? undefined : () => dispatch(addRoleToACLPendingDeletes(group))
                  }
                />
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
